import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { LINKED_IN_COLOR } from "../../../../constants/socialColors.constant";
import { boardApiService } from "../../../../cores/boards/services/api";
import { Contact } from "../../../../cores/contacts/interfaces/api/get-contact";
import { contactApiService } from "../../../../cores/contacts/services/api";
import { useMeCore } from "../../../../cores/me";
import "../ContactResearchActions.scss";

export interface ContactResearchFallback {
  name?: string;
  email?: string;
  phone?: string;
  companyName?: string;
}

interface Props {
  isOpen: boolean;
  contactId: string;
  fallback?: ContactResearchFallback;
  onClose: () => void;
}

type WebTab = "google" | "linkedin" | "news";

type WebResult = {
  id: string;
  source?: string;
  title: string;
  snippet?: string;
  url: string;
};

type WebBriefing = {
  summary?: string;
  findings?: Array<{ title?: string; detail?: string }>;
  questions?: string[];
};

export default function ContactOnlineInfoModal({
  isOpen,
  contactId,
  fallback,
  onClose,
}: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const [loadingContact, setLoadingContact] = useState(false);
  const [searching, setSearching] = useState(false);
  const [contact, setContact] = useState<Contact | null>(null);
  const [activeTab, setActiveTab] = useState<WebTab>("google");
  const [results, setResults] = useState<WebResult[]>([]);
  const [briefing, setBriefing] = useState<WebBriefing | null>(null);
  const [queryUsed, setQueryUsed] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const displayName =
    contact?.name ||
    fallback?.name ||
    contact?.email ||
    fallback?.email ||
    "—";

  const email = contact?.email || fallback?.email;
  const companyName =
    contact?.companies?.[0]?.name || fallback?.companyName;

  const canSearch = useMemo(
    () =>
      Boolean(
        String(displayName !== "—" ? displayName : "").trim() ||
          String(email || "").trim()
      ),
    [displayName, email]
  );

  const loadContact = useCallback(async () => {
    if (!company?.id || !contactId) return;
    setLoadingContact(true);
    try {
      const { contact: fullContact } = await contactApiService.getContact(
        company.id,
        contactId
      );
      setContact(fullContact);
    } catch (error) {
      console.error(error);
      setContact(null);
    } finally {
      setLoadingContact(false);
    }
  }, [company?.id, contactId]);

  const runSearch = useCallback(
    async (tab: WebTab) => {
      if (!company?.id || !canSearch) {
        toast.info(
          intl.formatMessage({
            id: "app.crmBusiness.contactResearch.needName",
          })
        );
        return;
      }
      setActiveTab(tab);
      setSearching(true);
      try {
        const response = await boardApiService.contactWebSearch(company.id, {
          name: displayName !== "—" ? displayName : undefined,
          email,
          companyName,
          tab,
        });
        const payload = response?.payload || response || {};
        setResults(payload.results || []);
        setBriefing(payload.briefing || null);
        setQueryUsed(payload.query || "");
      } catch (error) {
        console.error(error);
        setResults([]);
        setBriefing(null);
        toast.error(
          intl.formatMessage({
            id: "app.crmBusiness.contactResearch.searchError",
          })
        );
      } finally {
        setHasSearched(true);
        setSearching(false);
      }
    },
    [canSearch, company?.id, companyName, displayName, email, intl]
  );

  useEffect(() => {
    if (!isOpen) {
      setContact(null);
      setResults([]);
      setBriefing(null);
      setQueryUsed("");
      setActiveTab("google");
      setSearching(false);
      setHasSearched(false);
      return;
    }
    loadContact();
  }, [isOpen, loadContact]);

  useEffect(() => {
    if (!isOpen || loadingContact || !canSearch || hasSearched || searching) {
      return;
    }
    runSearch("google");
  }, [
    canSearch,
    hasSearched,
    isOpen,
    loadingContact,
    runSearch,
    searching,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="lg"
      zIndex={2100}
      className="contact-online-info-modal"
    >
      <ModalHeader toggle={onClose}>
        {intl.formatMessage({
          id: "app.crmBusiness.contactResearch.onlineInfoTitle",
        })}
      </ModalHeader>
      <ModalBody>
        <p className="contact-online-info__hint">
          {intl.formatMessage({
            id: "app.crmBusiness.contactResearch.onlineInfoHint",
          })}
        </p>
        <p className="contact-online-info__name-hint">
          {intl.formatMessage({
            id: "app.crmBusiness.contactResearch.fullNameHint",
          })}
        </p>

        {loadingContact ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            <div className="contact-online-info__meta">
              <div>
                <strong>
                  {intl.formatMessage({
                    id: "app.crmBusiness.contactResearch.person",
                  })}
                  :
                </strong>{" "}
                {displayName}
              </div>
              {email && (
                <div>
                  <strong>Email:</strong> {email}
                </div>
              )}
              {companyName && (
                <div>
                  <strong>
                    {intl.formatMessage({
                      id: "app.crmBusiness.contactResearch.company",
                    })}
                    :
                  </strong>{" "}
                  {companyName}
                </div>
              )}
            </div>

            <div className="contact-online-info__section">
              <div className="contact-online-info__web-actions">
                <Button
                  size="sm"
                  color={activeTab === "google" ? "primary" : "secondary"}
                  outline={activeTab !== "google"}
                  className="contact-online-info__chip"
                  onClick={() => runSearch("google")}
                  disabled={!canSearch || searching}
                >
                  <FontAwesomeIcon icon={["fab", "google"]} className="mr-1" />
                  {intl.formatMessage({
                    id: "app.crmBusiness.contactResearch.searchGoogle",
                  })}
                </Button>
                <Button
                  size="sm"
                  color="secondary"
                  outline={activeTab !== "linkedin"}
                  className="contact-online-info__chip"
                  onClick={() => runSearch("linkedin")}
                  disabled={!canSearch || searching}
                  style={
                    activeTab === "linkedin"
                      ? {
                          backgroundColor: LINKED_IN_COLOR,
                          borderColor: LINKED_IN_COLOR,
                          color: "#fff",
                        }
                      : {
                          borderColor: LINKED_IN_COLOR,
                          color: LINKED_IN_COLOR,
                        }
                  }
                >
                  <FontAwesomeIcon
                    icon={["fab", "linkedin"]}
                    className="mr-1"
                  />
                  LinkedIn
                </Button>
                <Button
                  size="sm"
                  color={activeTab === "news" ? "primary" : "secondary"}
                  outline={activeTab !== "news"}
                  className="contact-online-info__chip"
                  onClick={() => runSearch("news")}
                  disabled={!canSearch || searching}
                >
                  <FontAwesomeIcon
                    icon={["fas", "newspaper"]}
                    className="mr-1"
                  />
                  {intl.formatMessage({
                    id: "app.crmBusiness.contactResearch.news",
                  })}
                </Button>
              </div>

              <div className="contact-online-info__viewer">
                <div className="contact-online-info__viewer-bar">
                  <span>
                    {intl.formatMessage({
                      id: "app.crmBusiness.contactResearch.viewerLabel",
                    })}
                    {queryUsed ? ` · ${queryUsed}` : ""}
                  </span>
                </div>

                {searching ? (
                  <div className="contact-online-info__viewer-empty">
                    <Spinner size="sm" className="mr-2" />
                    {intl.formatMessage({
                      id: "app.crmBusiness.contactResearch.searching",
                    })}
                  </div>
                ) : (
                  <div className="contact-online-info__results">
                    {results.length ? (
                      results.map((item) => (
                        <a
                          key={item.id}
                          className={cn(
                            "contact-online-info__result",
                            item.source
                              ? `contact-online-info__result--${item.source}`
                              : ""
                          )}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <strong>{item.title}</strong>
                          {item.snippet ? <span>{item.snippet}</span> : null}
                          <small>{item.url}</small>
                        </a>
                      ))
                    ) : (
                      <div className="contact-online-info__viewer-empty">
                        {intl.formatMessage({
                          id: "app.crmBusiness.contactResearch.viewerEmpty",
                        })}
                      </div>
                    )}

                    {briefing?.summary ? (
                      <div className="contact-online-info__summary">
                        <strong>
                          {intl.formatMessage({
                            id: "app.crmBusiness.contactResearch.aiSummaryLabel",
                          })}
                        </strong>
                        <p>{briefing.summary}</p>
                        {!!briefing.findings?.length && (
                          <ul>
                            {briefing.findings.map((f, idx) => (
                              <li key={`${f.title}-${idx}`}>
                                <strong>{f.title}</strong>
                                {f.detail ? ` — ${f.detail}` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                        {!!briefing.questions?.length && (
                          <div className="contact-online-info__questions">
                            <strong>
                              {intl.formatMessage({
                                id: "app.crmBusiness.contactResearch.questionsLabel",
                              })}
                            </strong>
                            <ul>
                              {briefing.questions.map((q) => (
                                <li key={q}>{q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            <Button
              size="sm"
              color="primary"
              className="contact-online-info__ai-btn"
              onClick={() => runSearch(activeTab)}
              disabled={searching || !canSearch}
            >
              {searching ? (
                <Spinner size="sm" className="mr-2" />
              ) : (
                <FontAwesomeIcon
                  icon={["fas", "wand-magic-sparkles"] as any}
                  className="mr-2"
                />
              )}
              {intl.formatMessage({
                id: "app.crmBusiness.contactResearch.generateAiSummary",
              })}
            </Button>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline size="sm" onClick={onClose}>
          {intl.formatMessage({ id: "app.crmBusiness.contactResearch.close" })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
